<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
      <h1 class="flex-0 text-3xl leading-9 font-medium font-headline antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
        Promotions
      </h1>
      <FormButton v-if="GlobalPolicy.edit()" :to="{ name: 'promotion-add' }" prefix-class="fak" icon="fa-plus" color="blue">Add New Promotion</FormButton>
    </header>
    <div class="flex flex-col mt-4 p-4 rounded-lg">
      <div class="inline-flex flex-col flex-wrap md:flex-row md:justify-start gap-6">
        <div class="mb-4 flex-initial">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" color="gray" placeholder="Search..." />
        </div>
        <div class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.type" label="Type" :options="typeOptions" all="All Types" />
        </div>
        <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
          <FormButton @click="clear" class="bg-bhav-blue-500 text-white">Clear Filters</FormButton>
        </div>
      </div>

      <PaginationTable
        empty-message="There are currently no promotions."
        v-if="!isLoading"
        :legacy="false"
        :columns="columns"
        view="promotions-list"
        :data="filtered">
        <template #col-headline="{row}">
          <div class="block py-4 whitespace-nowrap">
            <div class="flex items-center">
              <router-link
                :to="{ name: 'promotion-show', params: { id: row.id } }"
                class="text-sm leading-5 font-medium text-bhav-blue-500 hover:underline">
                {{ row.headline }}
              </router-link>
            </div>
          </div>
        </template>
        <template #col-type="{row}">
          <div class="py-4 whitespace-nowrap">
            <span class="px-3 h-8 items-center justify-center inline-flex text-xs leading-5 font-semibold rounded-3xl bg-bhav-cool-gray-100 text-bhav-cool-gray-700">
              {{ row.type }}
            </span>
          </div>
        </template>
        <template #col-dates="{row}">
          <div class="py-4 whitespace-nowrap text-sm text-bhav-cool-gray-700">
            {{ formatDate(row.start_at) }} — {{ formatDate(row.end_at) }}
          </div>
        </template>
        <template #col-actions="{row}">
          <div class="py-4 flex justify-end">
            <FormButton type="button" @click="openDelete(row)" title="Permanently Delete Promotion" prefix-class="fak" icon="fa-trash" color="red" />
          </div>
        </template>
      </PaginationTable>
    </div>
  </div>

  <ModalDialog :no-padding="true" :open="!!deleting" @close="deleting = false">
    <template #header>
      <h2 class="text-lg text-bhav-blue-500 font-semibold">Confirm Promotion Deletion</h2>
    </template>
    <template #body>
      <p class="text-oakwood-gray-900"><strong>Attention!</strong> You are about to delete <em>{{ deleting?.headline }}</em>.</p>
      <p class="mt-2"><strong>This will <em>permanently</em> delete this promotion and its images.</strong></p>
      <p class="mt-2">Are you sure?</p>
    </template>
    <template #buttons>
      <FormButton type="button" @click="destroy" color="red">Delete</FormButton>
      <FormButton type="button" @click="deleting = false" color="gray">Cancel</FormButton>
    </template>
  </ModalDialog>
</template>

<script>
import LoadingView from '@/components/LoadingView.vue';
import { SelectMultipleField, FormButton, SearchInput } from '@/components/form';
import { mapState, mapWritableState, mapActions } from 'pinia';
import { usePromotionStore } from "@/stores/promotions";
import PaginationTable from "@/components/PaginationTable.vue";
import { GlobalPolicy } from '@/policies/global';
import ModalDialog from "@/components/ModalDialog.vue";
import { PromotionsController } from '@/controllers/promotions';

export default {
  setup() {
    return { GlobalPolicy }
  },
  data() {
    return {
      promotions: [],
      isLoading: true,
      deleting: false,
      typeOptions: [
        { label: 'Consumer', value: 'Consumer' },
        { label: 'Agent', value: 'Agent' },
        { label: 'NonListing', value: 'NonListing' }
      ],
      columns: [
        { name: 'headline', label: 'Headline', default: true, sort: (item) => item.headline || '' },
        { name: 'type', label: 'Type', default: true, sort: (item) => item.type || '' },
        { name: 'dates', label: 'Date Range', default: true, sort: (item) => item.start_at || '' },
        ...(GlobalPolicy.edit() ? [{ name: 'actions', label: '', default: true }] : [])
      ]
    }
  },
  methods: {
    ...mapActions(usePromotionStore, ['clear']),
    index() {
      PromotionsController.index()
        .then(promotions => {
          this.promotions = promotions;
          this.isLoading = false;
        })
        .catch(e => {
          throw e;
        });
    },
    formatDate(date) {
      if (!date) return '—';
      return new Date(date).toLocaleDateString();
    },
    openDelete(row) {
      this.deleting = row;
    },
    destroy() {
      PromotionsController.delete(this.deleting)
        .then(() => {
          this.promotions = this.promotions.filter(p => p.id !== this.deleting.id);
          this.deleting = false;
        })
        .catch(err => {
          this.deleting = false;
          console.error(err);
        });
    }
  },
  computed: {
    ...mapState(usePromotionStore, ['hasFilters']),
    ...mapWritableState(usePromotionStore, ['query', 'page']),
    filtered() {
      let results = this.promotions;

      if (this.query.type.length > 0) {
        results = results.filter(item => this.query.type.includes(item.type));
      }

      if (this.query.search != '') {
        const searchTerm = this.query.search.toLowerCase();
        results = results.filter(item => {
          const headline = (item.headline || '').toLowerCase();
          const description = (item.description || '').toLowerCase();
          return headline.includes(searchTerm) || description.includes(searchTerm);
        });
      }

      return results;
    }
  },
  watch: {
    query: {
      handler() {
        this.page = 1;
      },
      deep: true
    }
  },
  created() {
    this.index();
  },
  components: { LoadingView, PaginationTable, SelectMultipleField, FormButton, SearchInput, ModalDialog },
}
</script>
